import { pipelineFacts, pipelineVerdict } from './verdicts'
import {
  Duration,
  Node,
  Panel,
  Pipe,
  StarMetric,
  useVertical,
  withAccount,
} from './parts'
import { PIPELINE_HINTS as HINT } from './hints'
import {
  fmtSeconds,
  fmtHours,
  fmtCount,
  fmtCoverage,
  fmtPct,
  stopWords,
} from '../StreamIngest/vocabulary'

// The pipeline's row, audio in to quotes out (README.md, "Stages", says what
// each stage reads and how its status is decided): the Overview draws it above
// the loop's, and the Pipeline page heads its detail with it. Its popups are
// `hints.js`'s.
//
// `reads` is `usePipelineReads`'s answer. `stratified` is a Pipeline page
// narrowed by seal cause or client build. `summarized` is a page whose phone
// summary carries the row's sentence and the product's number (the
// Overview's), so the panel leaves them out when stacked. `to` is the page the
// row's name links to, none on that page itself. Every link carries `user`.

// Sums one counts map (`moments`) across a window's day rows.
const sumDayRows = (rows, field) =>
  rows.reduce((sum, row) => {
    Object.entries(row[field] || {}).forEach(([key, n]) => {
      if (typeof n === 'number') sum[key] = (sum[key] || 0) + n
    })
    return sum
  }, {})

const PipelinePanel = ({
  reads,
  user = null,
  stratified = false,
  summarized = false,
  to = '/pipeline',
}) => {
  const vertical = useVertical()
  // A stage's link to the Pipeline page's panel that explains it, on the
  // account this row is narrowed to.
  const pipelineAt = (section) => withAccount(`/pipeline#${section}`, user)
  // The window the numbers were read for, not the one being read.
  const { payload, capture, pipeline, totals, shownDays: windowDays } = reads

  // A pipe's time is the window's, as its stages' counts are. An API from
  // before the window's lags were sent falls back to the newest day's.
  const transport =
    payload?.window?.transport || payload?.days?.[0]?.transport || null
  const lag = (key, p = 'p50_s') => transport?.[key]?.[p] ?? null
  const {
    fleet,
    me,
    today,
    lost,
    lostHours,
    notUploaded,
    stuck,
    inFlight,
    stalled,
    oldestStalledH,
    belowBar,
  } = pipelineFacts({
    payload,
    capture,
    totals,
  })

  // One refresh's worth of events, from the pulse. `at` keys the burst so a
  // fresh payload replays the crossing and a repeated one does not.
  const pulse = capture?.pulse || null
  const declaredBurst = pulse
    ? {
        id: pulse.at,
        count: pulse.declared_since,
        truncated: pulse.truncated,
      }
    : null
  const segmentsBurst = pulse
    ? { id: pulse.at, count: pulse.pieces_since, truncated: pulse.truncated }
    : null
  // The third pipe: every segment the router judged this tick, routed or
  // not — the fork is drawn at the stage, not on the pipe — and the classes
  // heard in them. Older deploys' pulses carry neither.
  const judgedBurst =
    pulse && pulse.segments_routed_since != null
      ? {
          id: pulse.at,
          count:
            pulse.segments_routed_since +
            (pulse.segments_skipped_since || 0) +
            (pulse.segments_expired_since || 0),
          truncated: pulse.truncated,
        }
      : null
  const heard = pulse
    ? { id: pulse.at, items: pulse.classes_since || [] }
    : null

  const routerStats = pipeline?.router || null
  const backlog = pipeline?.backlog || null
  // Step 2b's block. An API deploy that predates it carries none, and the
  // stage stays drawn as not built.
  const asr = pipeline?.transcription || null
  // `1d` is today so far (UTC), so its totals read as today's.
  const windowLabel = windowDays === 1 ? 'today' : `last ${windowDays}d`
  // Waiting for a worker — the sweep's own set; in-flight and backing-off
  // spans are their own lines, so running work never paints this amber.
  const asrBacklog = asr?.backlog || null
  // The alarm reads every untranscribed span, whatever it waits on: one
  // backing off ages toward its 30 days all the same.
  const asrUntranscribed = asr?.untranscribed || asrBacklog
  // A timed-out read is unknown, never zero: the alarm must not go quiet
  // exactly when Mongo is struggling.
  const asrUnknown = Boolean(
    asrBacklog?.timed_out || asrUntranscribed?.timed_out
  )
  const asrPastAlarm =
    asrUntranscribed &&
    asr.alarm_age_h != null &&
    (asrUntranscribed.oldest_age_h || 0) > asr.alarm_age_h
  const asrUnavailable = asr?.unavailable?.spans || 0
  const skippedByReason = asr?.skipped_hours_by_reason || {}
  const unrouted = backlog
    ? (backlog.unrouted_durable?.spans || 0) +
      (backlog.unrouted_abandoned?.spans || 0)
    : 0
  const oldestUnroutedH = backlog
    ? Math.max(
        backlog.unrouted_durable?.oldest_age_h || 0,
        backlog.unrouted_abandoned?.oldest_age_h || 0
      )
    : 0
  const pastAlarm =
    backlog &&
    backlog.alarm_age_h != null &&
    oldestUnroutedH > backlog.alarm_age_h

  // ---- stage statuses -----------------------------------------------------
  const phones = me
    ? me.recording === true
      ? 'ok'
      : 'idle'
    : fleet
    ? fleet.silent > 0
      ? 'error'
      : fleet.recording_now > 0
      ? 'ok'
      : 'idle'
    : stratified
    ? 'pending'
    : 'idle'
  const declaredStatus = totals.declared > 0 ? 'ok' : 'idle'
  const upload =
    lost > 0
      ? 'error'
      : stuck > 0 || stalled > 0
      ? 'warning'
      : totals.declared > 0
      ? 'ok'
      : 'idle'
  const safe = totals.durable > 0 ? (belowBar > 0 ? 'warning' : 'ok') : 'idle'
  // Judged by loss, not latency, like every other stage: red is a span the
  // router has not reached past the alarm age (audio dies at 30 days), amber
  // is a backlog inside it, green is spans routed in the window.
  const processing = !pipeline
    ? 'pending'
    : pastAlarm
    ? 'error'
    : unrouted > 0
    ? 'warning'
    : routerStats && routerStats.spans_routed > 0
    ? 'ok'
    : 'idle'
  // Judged by loss like the router: red is a span untranscribed past the
  // alarm age, amber a backlog or an outage backing off, green hours moved.
  const transcribed = !asr
    ? 'pending'
    : asrPastAlarm
    ? 'error'
    : asrUnknown || (asrBacklog?.spans || 0) > 0 || asrUnavailable > 0
    ? 'warning'
    : asr.transcribed_hours > 0
    ? 'ok'
    : 'idle'
  // Step 4a: the window's quotes off the pipeline fold's selection block, its
  // per-day rows summed (the server sends only the window's days). Step 5: how
  // many of the window's quotes wait for their owner's review and how many
  // were kept.
  const selection = pipeline?.selection
  const selectionDays = selection?.days || []
  const quotesInWindow = sumDayRows(selectionDays, 'moments')
  const selectionBacklog = selection?.backlog || {}
  const quotes = !selection
    ? 'pending'
    : (selectionBacklog.terminal || 0) > 0
    ? 'warning'
    : (quotesInWindow.surfaced || 0) > 0
    ? 'ok'
    : 'idle'
  // Withheld is by design, so it is said in grey and never colours the stage.
  const mediaMode = selection?.wiring?.media_mode || 'shadow'
  const withheld = quotesInWindow.withheld ?? quotesInWindow.media_flagged ?? 0
  const mediaLine =
    mediaMode === 'withhold'
      ? `${fmtCount(withheld)} withheld as TV or music`
      : mediaMode === 'shadow'
      ? `${fmtCount(withheld)} sound like TV or music`
      : null
  // The product half's lags over the window, as the pipes' are.
  const productLags =
    payload?.product?.window || payload?.product?.days?.[0] || null
  // What the router turned aside, as a share of the segments it judged: the
  // same denominator as the routed fraction, never a share of speech.
  const judged = routerStats
    ? routerStats.segments_routed + routerStats.segments_skipped
    : 0
  const skippedShare = judged > 0 ? routerStats.segments_skipped / judged : null
  const skippedHours =
    routerStats && routerStats.captured_hours != null
      ? Math.max(
          0,
          Math.round(
            (routerStats.captured_hours - routerStats.routed_hours) * 100
          ) / 100
        )
      : null
  const levelGate = routerStats?.level_gate || null
  const durablePct =
    totals.declared > 0
      ? `${Math.round((totals.durable / totals.declared) * 100)}%`
      : '—'

  // ---- phones node, fleet or one account ----------------------------------
  const phonesNode = me
    ? {
        value:
          me.recording == null ? '—' : me.recording ? 'Mic live' : 'Mic off',
        lines: [
          `last declared ${
            me.silent_for_s == null
              ? 'never'
              : `${fmtSeconds(me.silent_for_s)} ago`
          }`,
          `last safe ${
            me.unsealed_for_s == null
              ? 'never'
              : `${fmtSeconds(me.unsealed_for_s)} ago`
          }`,
          ...(me.last_stop_reason ? [`last stop: ${me.last_stop_reason}`] : []),
        ],
        alert: null,
        hint: HINT.phonesAccount,
      }
    : {
        value: fleet ? fmtCount(fleet.recording_now) : '—',
        lines: [
          // A span's cause or build cannot narrow a count of accounts that are
          // sending no spans, so the endpoint withholds it under a stratum.
          // Saying so is the point: a bare dash reads as "nobody is
          // recording", which is the opposite of what is known.
          stratified ? 'not measurable under a filter' : 'recording right now',
          ...(today ? [`${fmtCount(today.users)} streamed today`] : []),
          ...(fleet ? [`${fmtCount(fleet.enrolled)} enrolled`] : []),
        ],
        alert:
          fleet && fleet.silent > 0
            ? {
                tone: 'error',
                text: `${fleet.silent} recording, nothing arriving`,
              }
            : null,
        // How the window's recordings ended, in grey: information, never a
        // status. A death is most often a quit or an iOS kill the phone
        // explains when it is next opened, so it says nothing to act on alone.
        died: fleet?.died || 0,
        stopped: Object.entries(fleet?.stopped || {}),
        hint: stratified
          ? HINT.phonesFiltered
          : fleet
          ? HINT.phones(fleet.threshold_minutes)
          : '',
      }

  const verdict = pipelineVerdict(reads, { user })
  const leads = !(vertical && summarized)

  return (
    <Panel
      title="Pipeline · audio → quotes"
      to={withAccount(to, user)}
      status={verdict.status}
      verdict={leads ? verdict : null}
      aside={leads ? <StarMetric payload={payload} /> : null}
    >
      <Node
        icon="📱"
        title="Phones"
        status={phones}
        value={phonesNode.value}
        lines={phonesNode.lines}
        alert={phonesNode.alert}
        hint={phonesNode.hint}
        diversions={[
          phonesNode.died || phonesNode.stopped?.length
            ? {
                label: `stopped · ${windowLabel}`,
                lines: [
                  phonesNode.died
                    ? `${fmtCount(phonesNode.died)} died, not opened since`
                    : null,
                  phonesNode.stopped
                    .map(([reason, n]) => `${fmtCount(n)} ${stopWords(reason)}`)
                    .join(' · ') || null,
                ].filter(Boolean),
                hint: HINT.phonesStopped,
              }
            : null,
        ]}
        link={{ to: pipelineAt('capture'), label: 'capture health' }}
      />
      <Pipe
        status={declaredStatus}
        burst={declaredBurst}
        unit="spans"
        label={<Duration>{fmtSeconds(lag('end_to_declared'))}</Duration>}
        hint={HINT.declaredPipe}
      />
      <Node
        icon="📦"
        title="Spans declared"
        status={declaredStatus}
        value={fmtCount(totals.declared)}
        lines={[
          windowLabel,
          ...(inFlight - stalled > 0
            ? [`${fmtCount(inFlight - stalled)} uploading`]
            : []),
        ]}
        alert={
          stuck > 0
            ? { tone: 'warning', text: `${fmtCount(stuck)} stuck` }
            : stalled > 0
            ? {
                tone: 'warning',
                text: `${fmtCount(stalled)} with no audio · oldest ${fmtHours(
                  oldestStalledH
                )}`,
              }
            : null
        }
        hint={HINT.declared}
        diversions={[
          notUploaded &&
          notUploaded.waiting + notUploaded.newInstall + notUploaded.gone > 0
            ? {
                label: `not uploaded · ${windowLabel}`,
                lines: [
                  notUploaded.waiting
                    ? `${fmtCount(
                        notUploaded.waiting
                      )} on a phone not opened since · oldest ${fmtHours(
                        notUploaded.oldestWaitingH
                      )}`
                    : null,
                  notUploaded.newInstall
                    ? `${fmtCount(
                        notUploaded.newInstall
                      )} left on a replaced install`
                    : null,
                  notUploaded.gone
                    ? `${fmtCount(notUploaded.gone)} gone: no word in ${
                        notUploaded.goneAfterDays
                      } days`
                    : null,
                ].filter(Boolean),
                hint: HINT.notUploaded,
              }
            : null,
        ]}
        link={{ to: pipelineAt('how-spans-end'), label: 'how spans end' }}
      />
      <Pipe
        status={upload}
        burst={segmentsBurst}
        unit="segments"
        label={<Duration>{fmtSeconds(lag('declared_to_sealed'))}</Duration>}
        hint={HINT.uploadPipe}
        leak={
          lost > 0
            ? `${fmtCount(lost)} lost · ${fmtHours(lostHours)} not safe`
            : null
        }
      />
      <Node
        icon="☁️"
        title="Safe on the server"
        status={safe}
        value={durablePct}
        lines={[
          `${fmtCount(totals.durable)} spans sealed`,
          ...(today
            ? [
                `${fmtHours(today.audio_hours)} of audio held today`,
                `coverage ${fmtCoverage(today.coverage.p50)} median`,
              ]
            : []),
        ]}
        alert={
          belowBar > 0
            ? {
                tone: 'warning',
                text: `${fmtCount(belowBar)} with holes`,
              }
            : null
        }
        hint={HINT.safe}
        link={{ to: pipelineAt('durability'), label: 'durability by day' }}
      />
      <Pipe
        status={processing}
        burst={judgedBurst}
        unit="segments judged"
        label={
          processing === 'pending' ? (
            'step 2a'
          ) : (
            <Duration>{fmtSeconds(lag('sealed_to_routed'))}</Duration>
          )
        }
        hint={HINT.routedPipe}
      />
      <Node
        icon="🔀"
        title="Routed"
        status={processing}
        value={routerStats ? fmtPct(routerStats.routed_fraction) : '—'}
        lines={
          routerStats
            ? [
                'of segments routed on',
                `${fmtPct(
                  routerStats.speech_fraction
                )} speech in captured audio`,
                `${fmtCount(routerStats.segments_routed)} routed · ${fmtCount(
                  routerStats.spans_routed
                )} span${routerStats.spans_routed === 1 ? '' : 's'}`,
                ...(unrouted > 0
                  ? [
                      `${fmtCount(unrouted)} span${
                        unrouted === 1 ? '' : 's'
                      } waiting · oldest ${fmtHours(oldestUnroutedH)}`,
                    ]
                  : []),
              ]
            : ['Silero VAD & YAMNet']
        }
        alert={
          pastAlarm
            ? {
                tone: 'error',
                text: `unrouted ${fmtHours(oldestUnroutedH)} · alarm ${fmtHours(
                  backlog.alarm_age_h
                )}`,
              }
            : null
        }
        chips={processing === 'pending' ? null : heard}
        diversions={
          routerStats
            ? [
                {
                  label: `kept, not transcribed · ${fmtPct(skippedShare)}`,
                  lines: [
                    `${fmtCount(routerStats.segments_skipped)} segments${
                      skippedHours != null ? ` · ${fmtHours(skippedHours)}` : ''
                    }${
                      routerStats.segments_expired
                        ? ` · ${fmtCount(
                            routerStats.segments_expired
                          )} expired unjudged`
                        : ''
                    }`,
                  ],
                  hint: HINT.routedSkipped,
                },
                levelGate && levelGate.shadow_drop_fraction != null
                  ? {
                      label: `level gate would skip ${fmtPct(
                        levelGate.shadow_drop_fraction
                      )} · shadow`,
                      shadow: true,
                      lines: [
                        `floor ${levelGate.floor_dbfs} dBFS, drops nothing`,
                      ],
                      hint: HINT.levelGate,
                    }
                  : null,
              ]
            : []
        }
        hint={HINT.routed}
        link={{ to: pipelineAt('spans-coming-in'), label: 'spans coming in' }}
      />
      <Pipe
        status={transcribed}
        label={
          transcribed === 'pending' ? (
            'step 2b'
          ) : (
            <Duration>{fmtSeconds(lag('sealed_to_processed'))}</Duration>
          )
        }
        hint={HINT.transcribedPipe}
      />
      <Node
        icon="📝"
        title="Transcribed"
        status={transcribed}
        value={asr ? fmtHours(asr.transcribed_hours) : '—'}
        lines={
          asr
            ? [
                `transcribed ${windowLabel}`,
                `${fmtHours(
                  asr.transcribed_premium_hours
                )} premium · ${fmtHours(asr.transcribed_bulk_hours)} bulk`,
                ...(asrBacklog && asrBacklog.spans > 0
                  ? [
                      `${fmtCount(asrBacklog.spans)} span${
                        asrBacklog.spans === 1 ? '' : 's'
                      } waiting · ${fmtHours(
                        asrBacklog.routed_hours
                      )} · oldest ${fmtHours(asrBacklog.oldest_age_h)}`,
                    ]
                  : []),
                ...(asr.allowance?.remaining_today_hours != null
                  ? [
                      `${fmtHours(
                        asr.allowance.remaining_today_hours
                      )} of today's allowance left${
                        asr.allowance.reconciled ? '' : ' · unreconciled'
                      }`,
                    ]
                  : []),
              ]
            : ['routed segments → text']
        }
        alert={
          asrPastAlarm
            ? {
                tone: 'error',
                text: `untranscribed ${fmtHours(
                  asrUntranscribed.oldest_age_h
                )} · alarm ${fmtHours(asr.alarm_age_h)}`,
              }
            : asrUnknown
            ? {
                tone: 'warning',
                text: 'backlog unknown · the metric read timed out',
              }
            : asrUnavailable > 0
            ? {
                tone: 'warning',
                text: `provider unavailable · ${fmtCount(asrUnavailable)} span${
                  asrUnavailable === 1 ? '' : 's'
                } backing off`,
              }
            : null
        }
        diversions={
          asr
            ? [
                Object.keys(skippedByReason).length
                  ? {
                      label: `skipped · ${fmtHours(asr.skipped_hours)}`,
                      lines: Object.entries(skippedByReason).map(
                        ([reason, row]) =>
                          `${reason} · ${fmtHours(row.hours)} · ${fmtCount(
                            row.runs
                          )} run${row.runs === 1 ? '' : 's'}`
                      ),
                      hint: HINT.transcribedSkipped,
                    }
                  : null,
                asr.would_skip_hours > 0
                  ? {
                      label: `credits would skip ${fmtHours(
                        asr.would_skip_hours
                      )} · ${asr.credit_mode}`,
                      shadow: asr.credit_mode === 'shadow',
                      lines: ['per-user bucket, drops nothing in shadow'],
                      hint: HINT.creditsWouldSkip,
                    }
                  : null,
              ]
            : []
        }
        hint={HINT.transcribed}
        link={{ to: pipelineAt('transcription'), label: 'transcription' }}
      />
      <Pipe
        status={quotes === 'pending' ? 'pending' : transcribed}
        label={
          quotes === 'pending' ? (
            'step 4a'
          ) : (
            <Duration>
              {fmtSeconds(productLags?.transcribed_to_selected?.p50_s ?? null)}
            </Duration>
          )
        }
        hint={HINT.selectedPipe}
      />
      <Node
        icon="✨"
        title="Quotes"
        status={quotes}
        value={selection ? fmtCount(quotesInWindow.surfaced || 0) : '—'}
        lines={
          selection
            ? [
                `surfaced ${windowLabel}`,
                [`${fmtCount(quotesInWindow.hidden || 0)} hidden`, mediaLine]
                  .filter(Boolean)
                  .join(' · '),
                `${fmtCount(
                  quotesInWindow.pending || 0
                )} awaiting review · ${fmtCount(
                  quotesInWindow.released || 0
                )} kept`,
                `tray → kept ${fmtSeconds(
                  productLags?.surfaced_to_released?.p50_s ?? null
                )}`,
                `${fmtCount(selectionBacklog.spans || 0)} spans waiting`,
              ]
            : ['picked by Gemini']
        }
        alert={
          (selectionBacklog.terminal || 0) > 0
            ? {
                tone: 'warning',
                text: `${fmtCount(selectionBacklog.terminal)} gave up`,
              }
            : null
        }
        hint={HINT.quotes[mediaMode] || HINT.quotes.off}
        link={{ to: withAccount('/moments', user), label: 'every quote' }}
      />
    </Panel>
  )
}

export default PipelinePanel
